import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, buttonPrimary, inputBase, pageHeading } from "../theme";
import HelpButton from "../components/HelpButton";
import { getAuthHeader, getLoginRedirect, getBusinessName } from "../session";

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [businessName, setBusinessName] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("waiter");
  const [pin, setPin] = useState("");
  const [shiftStart, setShiftStart] = useState("");
  const [shiftEnd, setShiftEnd] = useState("");
  const [error, setError] = useState("");
  const [cap, setCap] = useState({ max: null, active: 0 });
  const [editingShiftFor, setEditingShiftFor] = useState(null);
  const [shiftDraft, setShiftDraft] = useState({ shift_start: "", shift_end: "" });

  const authHeader = () => getAuthHeader();

  const load = () => {
    if (!getAuthHeader().Authorization) {
      window.location.href = getLoginRedirect();
      return;
    }
    axios.get(endpoints.staff, { headers: authHeader() })
      .then((res) => setStaff(res.data))
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          window.location.href = getLoginRedirect();
        }
      });

    // Live cap numbers (the login session's copy can be out of date after Alvna raises a limit).
    axios.get(endpoints.settings, { headers: authHeader() })
      .then((res) => setCap({ max: res.data.max_staff ?? null, active: res.data.active_staff_count ?? 0 }))
      .catch(() => {});

    setBusinessName(getBusinessName());
  };

  useEffect(load, []);

  const capReached = cap.max !== null && cap.active >= cap.max;

  const addStaff = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await axios.post(
        endpoints.staff,
        { name, role, pin_code: pin, shift_start: shiftStart || null, shift_end: shiftEnd || null },
        { headers: authHeader() }
      );
      setName("");
      setPin("");
      setShiftStart("");
      setShiftEnd("");
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not add staff member.");
      load();
    }
  };

  const deleteStaff = async (id, staffName) => {
    if (!window.confirm(`Remove "${staffName}"?`)) return;
    await axios.delete(endpoints.staffDelete(id), { headers: authHeader() });
    load();
  };

  const toggleStaffActive = async (id) => {
    setError("");
    try {
      await axios.patch(endpoints.staffToggleActive(id), {}, { headers: authHeader() });
    } catch (err) {
      setError(err.response?.data?.error || "Could not change that staff member.");
    }
    load();
  };

  const openShiftEditor = (s) => {
    setEditingShiftFor(s.id);
    setShiftDraft({ shift_start: (s.shift_start || "").slice(0, 5), shift_end: (s.shift_end || "").slice(0, 5) });
  };

  const saveShift = async (id) => {
    await axios.patch(
      endpoints.staffToggleActive(id),
      { shift_start: shiftDraft.shift_start || null, shift_end: shiftDraft.shift_end || null },
      { headers: authHeader() }
    );
    setEditingShiftFor(null);
    load();
  };

  return (
    <div style={{ display: "flex", background: colors.bgPage, minHeight: "100vh" }}>
      <Sidebar businessName={businessName} />
      <div className="app-content" style={{ flex: 1, padding: space[6], maxWidth: 760 }}>
        <h1 style={pageHeading}>Staff <HelpButton page="staff" /></h1>

        {cap.max !== null && (
          <div style={styles.capCounter} data-testid="staff-cap-counter">
            {cap.active} of {cap.max} staff accounts active
          </div>
        )}
        {capReached && (
          <div style={styles.capMessage} data-testid="staff-cap-message">
            Limit reached: max {cap.max} staff accounts. Upgrade to add more:{" "}
            <Link to="/settings" style={{ color: "inherit", fontWeight: 600 }}>request more under Settings</Link>.
          </div>
        )}
        <form className="inline-form" onSubmit={addStaff} style={styles.inlineForm}>
          <input
            style={styles.input}
            placeholder="Staff name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <select style={styles.select} value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="waiter">Waiter</option>
            <option value="kitchen">Kitchen</option>
            <option value="manager">Manager</option>
          </select>
          <input
            style={{ ...styles.input, width: 90 }}
            placeholder="4-digit PIN"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value)}
          />
          <input
            style={{ ...styles.input, width: 110 }}
            type="time"
            title="Shift start (optional)"
            value={shiftStart}
            onChange={(e) => setShiftStart(e.target.value)}
          />
          <input
            style={{ ...styles.input, width: 110 }}
            type="time"
            title="Shift end (optional)"
            value={shiftEnd}
            onChange={(e) => setShiftEnd(e.target.value)}
          />
          <button style={{ ...styles.addBtn, ...(capReached ? styles.addBtnDisabled : {}) }} type="submit" disabled={capReached}>Add Staff</button>
        </form>
        <p style={styles.hint}>
          Shift start/end are optional. Leave both blank for no schedule restriction (PIN works
          anytime while Active). Set both to automatically block that PIN outside those hours.
        </p>
        {error && <div style={styles.errorMessage}>{error}</div>}

        {staff.length === 0 ? (
          <p style={{ fontFamily: fonts.body, color: colors.textSecondary }}>No staff added yet.</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: `1px solid ${colors.border}` }}>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Shift</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <React.Fragment key={s.id}>
                  <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={styles.td}>{s.name}</td>
                    <td style={styles.td}>{s.role}</td>
                    <td style={styles.td}>{s.is_active ? "Active" : "Inactive"}</td>
                    <td style={styles.td}>
                      {s.shift_start && s.shift_end
                        ? `${s.shift_start.slice(0, 5)} to ${s.shift_end.slice(0, 5)}`
                        : "Any time"}
                    </td>
                    <td style={{ ...styles.td, display: "flex", gap: 10, alignItems: "center" }}>
                      <button
                        onClick={() => toggleStaffActive(s.id)}
                        style={s.is_active ? styles.deactivateBtn : styles.activateBtn}
                      >
                        {s.is_active ? "Deactivate" : "Activate"}
                      </button>
                      <button onClick={() => openShiftEditor(s)} style={styles.linkBtn}>
                        Edit Shift
                      </button>
                      <button onClick={() => deleteStaff(s.id, s.name)} style={styles.deleteLink}>
                        Remove
                      </button>
                    </td>
                  </tr>
                  {editingShiftFor === s.id && (
                    <tr style={{ background: colors.surfaceMuted }}>
                      <td colSpan="5" style={{ padding: space[3] }}>
                        <div style={{ display: "flex", gap: space[3], alignItems: "flex-end" }}>
                          <div>
                            <label style={styles.smallLabel}>Shift start</label>
                            <input
                              style={inputBase}
                              type="time"
                              value={shiftDraft.shift_start}
                              onChange={(e) => setShiftDraft({ ...shiftDraft, shift_start: e.target.value })}
                            />
                          </div>
                          <div>
                            <label style={styles.smallLabel}>Shift end</label>
                            <input
                              style={inputBase}
                              type="time"
                              value={shiftDraft.shift_end}
                              onChange={(e) => setShiftDraft({ ...shiftDraft, shift_end: e.target.value })}
                            />
                          </div>
                          <button onClick={() => saveShift(s.id)} style={styles.addBtn}>Save</button>
                          <button onClick={() => setEditingShiftFor(null)} style={styles.linkBtn}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const styles = {
  capCounter: { fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary, marginTop: space[2] },
  capMessage: { fontFamily: fonts.body, fontSize: 12, color: "#92400e", background: "#fef3c7", padding: space[2], borderRadius: radius.sm, marginTop: space[2] },
  addBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  inlineForm: { display: "flex", gap: space[2], marginTop: space[3], marginBottom: 8, flexWrap: "wrap" },
  input: { ...inputBase, flex: 1, padding: "8px 10px" },
  select: { ...inputBase, padding: "8px 10px" },
  addBtn: { ...buttonPrimary, padding: "8px 14px", whiteSpace: "nowrap" },
  hint: { fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, marginTop: 0, marginBottom: space[4] },
  smallLabel: { display: "block", fontFamily: fonts.body, fontSize: 11, color: colors.textSecondary, marginBottom: 4 },
  errorMessage: {
    background: "#fef3c7",
    color: "#92400e",
    padding: "8px 12px",
    borderRadius: radius.sm,
    fontSize: 13,
    fontFamily: fonts.body,
    marginBottom: 8,
  },
  table: { width: "100%", borderCollapse: "collapse", fontFamily: fonts.body },
  th: { padding: "8px 4px", fontSize: 13, color: colors.textSecondary },
  td: { padding: "8px 4px", fontSize: 14, color: colors.textPrimary },
  linkBtn: { background: "none", border: "none", color: colors.brandBlue, fontSize: 12, cursor: "pointer", padding: 0, fontFamily: fonts.body },
  deleteLink: { background: "none", border: "none", color: "#b91c1c", fontSize: 12, cursor: "pointer", padding: 0, fontFamily: fonts.body },
  activateBtn: {
    padding: "8px 16px", fontSize: 13, fontWeight: 600, fontFamily: fonts.body,
    borderRadius: radius.sm, border: "none", background: colors.statusReady, color: "#fff", cursor: "pointer",
  },
  deactivateBtn: {
    padding: "8px 16px", fontSize: 13, fontWeight: 600, fontFamily: fonts.body,
    borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: colors.surfaceMuted, color: colors.textPrimary, cursor: "pointer",
  },
};
